import {
  Archive,
  ChevronsUpDown,
  Database,
  Layers,
  Network,
  X,
  Zap,
} from 'lucide-react'
import { Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { PERIOD_OPTIONS, getUsageTypeLabel } from '@/routes/usage/constants'
import type {
  ProjectInfo,
  ServiceInfo,
  ServiceType,
  UsageFilterState,
  UsagePeriod,
} from '@/routes/usage/types'

type UsageFiltersProps = {
  filters: UsageFilterState
  onFiltersChange: (filters: UsageFilterState) => void
  projects: ProjectInfo[]
  services: ServiceInfo[]
  usageTypes: string[]
}

type SelectItem = {
  value: string
  label: string
  icon?: React.ReactNode
}

const SERVICE_TYPE_ICONS: Record<ServiceType, React.ReactNode> = {
  app: <Layers className="size-3.5 shrink-0 text-muted-foreground" />,
  db: <Database className="size-3.5 shrink-0 text-muted-foreground" />,
  staticSite: <Zap className="size-3.5 shrink-0 text-muted-foreground" />,
  objectStorage: (
    <Archive className="size-3.5 shrink-0 text-muted-foreground" />
  ),
  loadBalancer: <Network className="size-3.5 shrink-0 text-muted-foreground" />,
}

const MultiSelect = ({
  label,
  items,
  selected,
  onSelectedChange,
}: {
  label: string
  items: SelectItem[]
  selected: string[]
  onSelectedChange: (values: string[]) => void
}) => {
  const selectedSet = new Set(selected)

  const toggle = (value: string) => {
    const next = new Set(selectedSet)
    if (next.has(value)) {
      next.delete(value)
    } else {
      next.add(value)
    }
    onSelectedChange(Array.from(next))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 gap-1 text-sm">
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-1 px-1.5 text-xs">
              {selected.length}
            </Badge>
          )}
          <ChevronsUpDown className="ml-1 size-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-fit max-w-[350px] min-w-[180px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  onSelect={() => toggle(item.value)}
                >
                  <Checkbox
                    checked={selectedSet.has(item.value)}
                    className="pointer-events-none [&_svg]:!text-primary-foreground"
                  />
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const SingleSelect = ({
  items,
  value,
  onValueChange,
}: {
  items: SelectItem[]
  value: string
  onValueChange: (value: string) => void
}) => {
  const [open, setOpen] = useState(false)
  const selectedLabel = items.find((i) => i.value === value)?.label ?? value

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 gap-1 text-sm">
          {selectedLabel}
          <ChevronsUpDown className="ml-1 size-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[180px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  onSelect={() => {
                    onValueChange(item.value)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'size-3.5 shrink-0',
                      value === item.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export const UsageFilters = ({
  filters,
  onFiltersChange,
  projects,
  services,
  usageTypes,
}: UsageFiltersProps) => {
  const projectItems = useMemo(
    () =>
      projects.map((p) => ({
        value: p.id,
        label: p.displayName,
      })),
    [projects]
  )

  const serviceItems = useMemo(() => {
    const selectedProjectSet = new Set(filters.selectedProjects)
    const filtered =
      selectedProjectSet.size > 0
        ? services.filter((s) => selectedProjectSet.has(s.idProject))
        : services

    return filtered.map((s) => ({
      value: s.id,
      label: s.displayName,
      icon: s.serviceType ? SERVICE_TYPE_ICONS[s.serviceType] : undefined,
    }))
  }, [services, filters.selectedProjects])

  const typeItems = useMemo(
    () =>
      usageTypes.map((t) => ({
        value: t,
        label: getUsageTypeLabel(t),
      })),
    [usageTypes]
  )

  const hasActiveFilters =
    filters.selectedProjects.length > 0 ||
    filters.selectedServices.length > 0 ||
    filters.selectedTypes.length > 0

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      selectedProjects: [],
      selectedServices: [],
      selectedTypes: [],
    })
  }

  const periodItems = useMemo(
    () =>
      PERIOD_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      })),
    []
  )

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SingleSelect
        items={periodItems}
        value={filters.period}
        onValueChange={(value) =>
          onFiltersChange({ ...filters, period: value as UsagePeriod })
        }
      />

      {projectItems.length > 0 && (
        <MultiSelect
          label="Projects"
          items={projectItems}
          selected={filters.selectedProjects}
          onSelectedChange={(selectedProjects) =>
            onFiltersChange({
              ...filters,
              selectedProjects,
              selectedServices: filters.selectedServices.filter((sId) => {
                if (selectedProjects.length === 0) {
                  return true
                }
                const svc = services.find((s) => s.id === sId)
                return svc ? selectedProjects.includes(svc.idProject) : false
              }),
            })
          }
        />
      )}

      {serviceItems.length > 0 && (
        <MultiSelect
          label="Services"
          items={serviceItems}
          selected={filters.selectedServices}
          onSelectedChange={(selectedServices) =>
            onFiltersChange({ ...filters, selectedServices })
          }
        />
      )}

      {typeItems.length > 0 && (
        <MultiSelect
          label="Usage type"
          items={typeItems}
          selected={filters.selectedTypes}
          onSelectedChange={(selectedTypes) =>
            onFiltersChange({ ...filters, selectedTypes })
          }
        />
      )}

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 gap-1 text-sm"
          onClick={clearFilters}
        >
          Clear filters
          <X className="size-3.5" />
        </Button>
      )}
    </div>
  )
}
